import { ChevronLeft, ChevronRight } from 'lucide-react';

const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100];

interface TablePaginationProps {
  page: number; // 0-indexed
  pageSize: number;
  totalRows: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  rowLabel?: string; // singular noun ("record", "gap"); 's' is appended for plural
}

export default function TablePagination({
  page,
  pageSize,
  totalRows,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  rowLabel = 'record',
}: TablePaginationProps) {
  if (totalRows === 0) return null;

  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const firstShown = safePage * pageSize + 1;
  const lastShown = Math.min((safePage + 1) * pageSize, totalRows);
  const plural = totalRows === 1 ? '' : 's';

  return (
    <div className="border-t bg-muted/20 px-3 py-2 flex items-center justify-between gap-3 text-xs text-muted-foreground">
      <span>
        Showing{' '}
        <span className="font-medium text-foreground">
          {firstShown}–{lastShown}
        </span>{' '}
        of{' '}
        <span className="font-medium text-foreground">{totalRows}</span>{' '}
        {rowLabel}
        {plural}
      </span>
      <div className="flex items-center gap-3">
        {onPageSizeChange && (
          <label className="inline-flex items-center gap-1.5">
            <span>Per page</span>
            <select
              value={pageSize}
              onChange={(e) => {
                // React 18 auto-batches both setters in this handler into a
                // single render, so the table never flashes a stale page
                // index between updating size and resetting to the first page.
                onPageChange(0);
                onPageSizeChange(Number(e.target.value));
              }}
              className="rounded border bg-background text-foreground px-1.5 py-0.5 text-xs cursor-pointer hover:bg-muted transition-colors focus:outline-none focus:ring-1 focus:ring-primary"
              aria-label="Rows per page"
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        )}
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onPageChange(Math.max(0, safePage - 1))}
              disabled={safePage === 0}
              className="inline-flex items-center gap-1 px-2 py-1 rounded border bg-background hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Prev
            </button>
            <span className="px-1 tabular-nums">
              Page{' '}
              <span className="font-medium text-foreground">
                {safePage + 1}
              </span>{' '}
              of{' '}
              <span className="font-medium text-foreground">
                {totalPages}
              </span>
            </span>
            <button
              type="button"
              onClick={() =>
                onPageChange(Math.min(totalPages - 1, safePage + 1))
              }
              disabled={safePage >= totalPages - 1}
              className="inline-flex items-center gap-1 px-2 py-1 rounded border bg-background hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Next page"
            >
              Next
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
